import { motion } from 'motion/react'
import type React from 'react'
import { createPortal } from 'react-dom'
import { LuX } from 'react-icons/lu'

interface MotionModalContentProps {
  children: React.ReactNode
  className?: string
  closeButton?: boolean
  onClose?: () => void
  overlayClassName?: string
  position?: 'center' | 'left' | 'right'
  onPointerDownOutside?: (event: any) => void
  onEscapeKeyDown?: (event: any) => void
}

export default function MotionModalContent({
  children,
  className = '',
  closeButton = true,
  overlayClassName = '',
  onClose,
  position = 'center',
  onPointerDownOutside,
}: MotionModalContentProps): JSX.Element {
  const handleBackdropClick = (e: React.MouseEvent) => {
    if (e.target === e.currentTarget) {
      onPointerDownOutside?.(e)
      onClose?.()
    }
  }

  const positionClasses = {
    center: 'items-center justify-center',
    left: 'items-center justify-start pl-8',
    right: 'items-center justify-end pr-8',
  }

  const modalContent = (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      transition={{ duration: 0.2 }}
      className={`fixed inset-0 z-[100] flex ${positionClasses[position]} bg-black/50 backdrop-blur-sm ${overlayClassName || ''}`}
      onClick={handleBackdropClick}
    >
      <motion.div
        initial={{ scale: 0.95, opacity: 0 }}
        animate={{ scale: 1, opacity: 1 }}
        exit={{ scale: 0.95, opacity: 0 }}
        transition={{ duration: 0.2 }}
        className={`relative bg-white rounded-lg shadow-lg ${className}`}
        onClick={e => e.stopPropagation()}
      >
        {closeButton && (
          <button
            onClick={onClose}
            className="absolute right-4 top-4 rounded-sm opacity-70 ring-offset-background transition-opacity hover:opacity-100 focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2 disabled:pointer-events-none data-[state=open]:bg-accent data-[state=open]:text-muted-foreground"
          >
            <LuX className="h-4 w-4" />
            <span className="sr-only">Close</span>
          </button>
        )}
        {children}
      </motion.div>
    </motion.div>
  )

  return createPortal(modalContent, document.body)
}
